import React, { useState, useEffect, useRef, useCallback } from "react";
import { Button, Chip, Popover } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { StaticDatePicker } from "@mui/x-date-pickers/StaticDatePicker";
import packsIcon from "../../assets/new icons/dashboard/packs.svg";
import formsIcon from "../../assets/new icons/dashboard/forms.svg";
import whsIcon from "../../assets/new icons/dashboard/witholding statements.svg";
import idCardsIcon from "../../assets/new icons/dashboard/id cards.svg";
import lastImportIcon from "../../assets/new icons/dashboard/last import.svg";
import lastExtractionIcon from "../../assets/new icons/dashboard/last extraction.svg";
import lastExportIcon from "../../assets/new icons/dashboard/last export.svg";
import calendarIcon from "../../assets/new icons/dashboard/calendar.svg";
import exportPdfIcon from "../../assets/new icons/dashboard/export pdf.svg";
import downArrowIcon from "../../assets/new icons/arrow.svg";
import LoaderIndicator from "../../Utility/loading";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  Cell,
  LabelList,
  PieChart,
  Pie,
} from "recharts";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { handleLogout } from "../../Utility/utility";
import { BASE_URL } from "../../App_Settings/app_settings";
import {
  getUserIdFromLocalStorage,
  getUserFileAuditByUserId,
} from "../../API_Endpoints/APIEndpoints";
import "./dashboard.css";
import { screenHeaderStyle, useSharedValue } from "../Constants/constants";
import { useHistory } from "react-router-dom";

// ── Date Range Preset Helpers ──
const getDateRange = (preset) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  switch (preset) {
    case "today":
      return { start: today, end: now };
    case "7days": {
      const start = new Date(today);
      start.setDate(start.getDate() - 6);
      return { start, end: now };
    }
    case "30days": {
      const start = new Date(today);
      start.setDate(start.getDate() - 29);
      return { start, end: now };
    }
    case "thisMonth":
      return { start: new Date(now.getFullYear(), now.getMonth(), 1), end: now };
    case "thisYear":
      return { start: new Date(now.getFullYear(), 0, 1), end: now };
    case "all":
    default:
      return { start: null, end: null };
  }
};

const DATE_PRESETS = [
  { value: "all", label: "All Time" },
  { value: "today", label: "Today" },
  { value: "7days", label: "Last 7 Days" },
  { value: "30days", label: "Last 30 Days" },
  { value: "thisMonth", label: "This Month" },
  { value: "thisYear", label: "This Year" },
  { value: "custom", label: "Custom Range" },
];

// ── Read a CSS custom property value at runtime ──
const getCSSVar = (name) =>
  getComputedStyle(document.documentElement).getPropertyValue(name).trim();

// ── Custom Tooltip ──
const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div className="custom-tooltip">
        <p>{`${payload[0].payload.name}: ${payload[0].value}`}</p>
      </div>
    );
  }
  return null;
};

// ── Document Type Stat Card ──
const DocTypeCard = ({ icon, label, uploaded, extracted, color, bgColor }) => (
  <div
    className="db-doc-card"
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = "translateY(-1px)";
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = "translateY(0)";
    }}
  >
    <div className="db-doc-card-header">
      <div className="db-doc-card-icon-wrapper" style={{ backgroundColor: bgColor || "#E9F5FE" }}>
        {icon ? <img src={icon} alt="" className="db-doc-card-icon" /> : null}
      </div>
      <span className="db-doc-card-label">{label}</span>
    </div>
    <div className="db-doc-card-stats">
      <div className="db-doc-card-stat-box db-doc-card-stat-box--uploaded">
        <div className="db-doc-card-stat-value db-doc-card-stat-value--uploaded">
          {uploaded ?? "—"}
        </div>
        <div className="db-doc-card-stat-text db-doc-card-stat-text--uploaded">Uploaded</div>
      </div>
      <div className="db-doc-card-stat-box" style={{ backgroundColor: bgColor || "#E9F5FE" }}>
        <div className="db-doc-card-stat-value" style={{ color: color || "#2AA7E0" }}>
          {extracted ?? "—"}
        </div>
        <div className="db-doc-card-stat-text" style={{ color: color || "#2AA7E0" }}>
          Extracted
        </div>
      </div>
    </div>
  </div>
);

// ── Activity Badge ──
const ActivityBadge = ({ label, date, icon }) => (
  <div className="db-activity-badge">
    {icon && <img src={icon} alt={label} className="db-activity-badge-icon" />}
    <span className="db-activity-badge-label">{label}</span>
    <span className="db-activity-badge-date">{date || "N/A"}</span>
  </div>
);

export default function DashboardPage() {
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [datePreset, setDatePreset] = useState("all");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [calendarAnchor, setCalendarAnchor] = useState(null);
  const [pendingRange, setPendingRange] = useState([null, null]);

  const { defaultBusinessunit, defaultOrganization, defaultUser, isMenuOpen } = useSharedValue();

  // Dashboard activity timestamps
  const [lastExtracted, setLastExtracted] = useState(null);
  const [lastImported, setLastImported] = useState(null);
  const [lastExported, setLastExported] = useState(null);

  // Chart data
  const [fileProcessingFunnelData, setFileProcessingFunnelData] = useState([]);
  const [workflowStatusData, setWorkflowStatusData] = useState([]);
  const [fileLifecycleData, setFileLifecycleData] = useState([]);

  // Document type breakdown
  const [docTypeBreakdown, setDocTypeBreakdown] = useState({
    packsUploaded: 0,
    packsExtracted: 0,
    formsUploaded: 0,
    formsExtracted: 0,
    whsUploaded: 0,
    whsExtracted: 0,
    idDocsUploaded: 0,
    idDocsExtracted: 0,
  });

  // Refs
  const prevFiltersRef = useRef({
    businessunit: defaultBusinessunit,
    organization: defaultOrganization,
    user: defaultUser,
  });

  const formatStoredDate = (dateString) => {
    const date = dateString ? new Date(dateString) : new Date();
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
    });
  };

  // Handle date preset change
  const handleDatePresetChange = useCallback((preset) => {
    setDatePreset(preset);
    const { start, end } = getDateRange(preset);
    setStartDate(start);
    setEndDate(end);
    setPendingRange([start, end]);
    setCalendarAnchor(null);
  }, []);

  // Fetch dashboard data
  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      if (!isMounted) return;

      try {
        setLoading(true);
        const token = getUserIdFromLocalStorage().token;

        const params = new URLSearchParams();
        if (defaultUser && defaultUser.userId) {
          params.set("userId", defaultUser.userId);
        } else if (defaultUser && defaultUser.id) {
          params.set("userId", defaultUser.id);
        } else if (defaultOrganization && defaultOrganization.organizationId) {
          params.set("organizationId", defaultOrganization.organizationId);
        } else if (defaultOrganization && defaultOrganization.id) {
          params.set("organizationId", defaultOrganization.id);
        }

        if (startDate) {
          params.set("startDate", new Date(startDate).toISOString());
        }
        if (endDate) {
          params.set("endDate", new Date(endDate).toISOString());
        }

        const queryString = params.toString();
        const dashboardUrl = `${BASE_URL}/Dashboard/summary${queryString ? `?${queryString}` : ""}`;

        const response = await fetch(dashboardUrl, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
            ...(localStorage.getItem("sessionToken") && {
              "X-Session-Token": localStorage.getItem("sessionToken"),
            }),
          },
        });

        if (response.status === 401) {
          handleLogout(history);
          return;
        }

        if (!response.ok) {
          console.error("Dashboard API error:", response.status);
          return;
        }

        const data = await response.json();
        if (!isMounted) return;

        // Extraction Funnel (Bar Chart)
        const funnelData = [
          {
            name: "Uploaded",
            value: data.totalUploaded ?? 0,
            color: getCSSVar("--db-funnel-uploaded"),
          },
          {
            name: "Waiting",
            value: data.totalWaiting ?? 0,
            color: getCSSVar("--db-funnel-waiting"),
          },
          {
            name: "Extracted",
            value: data.totalExtracted ?? 0,
            color: getCSSVar("--db-funnel-extracted"),
          },
          {
            name: "Approved",
            value: data.totalApproved ?? 0,
            color: getCSSVar("--db-funnel-approved"),
          },
          {
            name: "Rejected",
            value: data.totalRejected ?? 0,
            color: getCSSVar("--db-funnel-rejected"),
          },
        ];
        setFileProcessingFunnelData(funnelData);
        setFileLifecycleData(funnelData);

        // Forms Statistics (Pie Chart)
        const formsChartData = [
          { name: "Pages", value: data.totalPages ?? 0, color: getCSSVar("--db-pie-pages") },
          { name: "Forms", value: data.totalForms ?? 0, color: getCSSVar("--db-pie-forms") },
          {
            name: "ID Documents",
            value: data.idDocuments ?? 0,
            color: getCSSVar("--db-pie-id-documents"),
          },
          { name: "WHS", value: data.withholdingStatements ?? 0, color: getCSSVar("--db-pie-whs") },
          { name: "Other", value: data.otherDocuments ?? 0, color: getCSSVar("--db-pie-other") },
        ];
        setWorkflowStatusData(formsChartData);

        // Activity Timestamps
        setLastImported(data.lastImported ? formatStoredDate(data.lastImported) : null);
        setLastExtracted(data.lastExtracted ? formatStoredDate(data.lastExtracted) : null);
        setLastExported(data.lastExported ? formatStoredDate(data.lastExported) : null);

        // Document type breakdown (Stat Cards)
        setDocTypeBreakdown({
          packsUploaded: data.packsUploaded ?? 0,
          packsExtracted: data.packsExtracted ?? 0,
          formsUploaded: data.formsUploaded ?? 0,
          formsExtracted: data.formsExtracted ?? 0,
          whsUploaded: data.whsUploaded ?? 0,
          whsExtracted: data.whsExtracted ?? 0,
          idDocsUploaded: data.idDocsUploaded ?? 0,
          idDocsExtracted: data.idDocsExtracted ?? 0,
        });

        // Document Type Breakdown
        setDocTypeBreakdown({
          packsUploaded: data.packsUploaded ?? 0,
          packsExtracted: data.packsExtracted ?? 0,
          formsUploaded: data.formsUploaded ?? 0,
          formsExtracted: data.formsExtracted ?? 0,
          whsUploaded: data.whsUploaded ?? 0,
          whsExtracted: data.whsExtracted ?? 0,
          idDocsUploaded: data.idDocsUploaded ?? 0,
          idDocsExtracted: data.idDocsExtracted ?? 0,
        });
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
        if (error.message && error.message.includes("401")) {
          handleLogout(history);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchData();

    prevFiltersRef.current = {
      businessunit: defaultBusinessunit,
      organization: defaultOrganization,
      user: defaultUser,
    };

    return () => {
      isMounted = false;
    };
  }, [defaultBusinessunit, defaultOrganization, defaultUser, startDate, endDate, history]);

  // PDF Report download
  const handleDownloadPDFReport = () => {
    setIsLoading(true);

    getUserFileAuditByUserId()
      .then((data) => {
        let filteredData = data;
        if (startDate && endDate) {
          filteredData = data.filter((item) => {
            const itemDate = new Date(item.createdDate);
            const startDateTime = new Date(startDate).setHours(0, 0, 0, 0);
            const endDateTime = new Date(endDate).setHours(23, 59, 59, 999);
            return itemDate >= startDateTime && itemDate <= endDateTime;
          });
        }

        const doc = generatePDF(filteredData, "Dashboard Report");
        doc.save(`dashboard-report-${new Date().toISOString().split("T")[0]}.pdf`);
        localStorage.setItem("downloadTime", new Date().toISOString());
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Error generating PDF report:", error);
        setIsLoading(false);
      });
  };

  const generatePDF = (data, reportName) => {
    const doc = new jsPDF();

    const logoPath = "/logo.png";
    doc.addImage(logoPath, "PNG", 14, 10, 50, 9);

    doc.setFontSize(15);
    doc.text(reportName, 14, 25);

    if (startDate && endDate) {
      const formattedStartDate = new Date(startDate).toLocaleDateString();
      const formattedEndDate = new Date(endDate).toLocaleDateString();
      doc.setFontSize(10);
      doc.text(`Date Range: ${formattedStartDate} to ${formattedEndDate}`, 14, 30);
    }

    const userInfo = getUserIdFromLocalStorage();
    const userName = `${userInfo.firstName} ${userInfo.lastName}` || "N/A";
    const userEmail = userInfo?.userName || "N/A";
    const organizationName = userInfo?.organizationName || "N/A";

    doc.setFontSize(10);
    doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 35);
    doc.text(`User: ${userName}`, 14, 40);
    doc.text(`Email: ${userEmail}`, 14, 45);
    doc.text(`Organization: ${organizationName}`, 14, 50);

    doc.setFontSize(12);
    doc.text("Extraction Statistics", 14, 65);
    autoTable(doc, {
      head: [["Stage", "Value"]],
      body: fileProcessingFunnelData.map((item) => [item.name, item.value]),
      startY: 70,
      theme: "grid",
      headStyles: { fillColor: [42, 167, 224] },
      styles: { fontSize: 8 },
    });

    const extractionTableEndY = doc.lastAutoTable?.finalY || 70;

    doc.setFontSize(12);
    doc.text("Forms Statistics", 14, extractionTableEndY + 15);
    autoTable(doc, {
      head: [["Form Type", "Count"]],
      body: workflowStatusData.map((item) => [item.name, item.value]),
      startY: extractionTableEndY + 20,
      theme: "grid",
      headStyles: { fillColor: [42, 167, 224] },
      styles: { fontSize: 8 },
    });

    const formsTableEndY = doc.lastAutoTable?.finalY || extractionTableEndY + 20;

    doc.text("Total Statistics", 14, formsTableEndY + 15);
    autoTable(doc, {
      head: [["Document Type", "Uploaded", "Extracted"]],
      body: [
        ["Packs", docTypeBreakdown.packsUploaded, docTypeBreakdown.packsExtracted],
        ["Forms", docTypeBreakdown.formsUploaded, docTypeBreakdown.formsExtracted],
        ["Withholding Statements", docTypeBreakdown.whsUploaded, docTypeBreakdown.whsExtracted],
        ["ID Documents", docTypeBreakdown.idDocsUploaded, docTypeBreakdown.idDocsExtracted],
      ],
      startY: formsTableEndY + 20,
      theme: "grid",
      headStyles: { fillColor: [42, 167, 224] },
      styles: { fontSize: 8 },
    });

    return doc;
  };

  // Compute totals for stat cards from document type breakdown
  const {
    packsUploaded,
    packsExtracted,
    formsUploaded,
    formsExtracted,
    whsUploaded,
    whsExtracted,
    idDocsUploaded,
    idDocsExtracted,
  } = docTypeBreakdown;

  const userInfo = getUserIdFromLocalStorage();
  const userName = `${userInfo.firstName || ""} ${userInfo.lastName || ""}`.trim();
  const selectedPresetLabel = DATE_PRESETS.find((p) => p.value === datePreset)?.label || "All Time";

  return (
    <div
      className={screenHeaderStyle(isMenuOpen)}
      style={{
        overflow: "hidden",
        height: "calc(100vh - 64px)",
        backgroundColor: "#f0f2f5",
        marginTop: 0,
        marginLeft: 0,
        marginRight: 0,
        borderRadius: 0,
        width: "100%",
      }}
    >
      <div className="db-scroll-container">
        {/* ── Top Bar: Activity Badges (left) + Date Selector + Actions (right) ── */}
        <div className="db-top-bar">
          {/* Left: Activity Badges */}
          <div className="db-activity-badges">
            <ActivityBadge label="Last Import:" date={lastImported} icon={lastImportIcon} />
            <ActivityBadge
              label="Last Extraction:"
              date={lastExtracted}
              icon={lastExtractionIcon}
            />
            <ActivityBadge label="Last Export:" date={lastExported} icon={lastExportIcon} />
          </div>

          {/* Right: Date Range + Export PDF */}
          <div className="db-top-right">
            {/* Calendar button */}
            <button
              onClick={(e) => {
                setPendingRange([startDate, endDate]);
                setCalendarAnchor(e.currentTarget);
              }}
              className="db-calendar-btn"
            >
              <img src={calendarIcon} alt="calendar" style={{ width: 18, height: 18 }} />
              <span className="db-calendar-btn-text">
                {DATE_PRESETS.find((p) => p.value === datePreset)?.label || "All Time"}
              </span>
              <img src={downArrowIcon} alt="open" style={{ width: 10, height: 10 }} />
            </button>

            {/* Calendar Popover */}
            <Popover
              open={Boolean(calendarAnchor)}
              anchorEl={calendarAnchor}
              onClose={() => setCalendarAnchor(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              transformOrigin={{ vertical: "top", horizontal: "left" }}
              PaperProps={{
                style: {
                  borderRadius: 12,
                  boxShadow: "none",
                  overflow: "hidden",
                },
              }}
            >
              {/* Preset chips */}
              <div className="db-date-presets">
                {DATE_PRESETS.map((preset) => (
                  <button
                    key={preset.value}
                    onClick={() => handleDatePresetChange(preset.value)}
                    className={`db-preset-btn${datePreset === preset.value ? " db-preset-btn--active" : ""}`}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
              {/* Date range calendar — two free StaticDatePickers */}
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <div className="db-calendar-pickers">
                  <div className="db-calendar-start-col">
                    <div className="db-calendar-picker-header">Start Date</div>
                    <StaticDatePicker
                      displayStaticWrapperAs="desktop"
                      value={pendingRange[0]}
                      onChange={(newDate) => {
                        const newRange = [newDate, pendingRange[1]];
                        setPendingRange(newRange);
                        if (newDate && pendingRange[1]) {
                          setDatePreset("custom");
                          setStartDate(newDate);
                          setEndDate(pendingRange[1]);
                          setCalendarAnchor(null);
                        }
                      }}
                      renderInput={() => null}
                      componentsProps={{ actionBar: { actions: [] } }}
                    />
                  </div>
                  <div>
                    <div className="db-calendar-picker-header">End Date</div>
                    <StaticDatePicker
                      displayStaticWrapperAs="desktop"
                      value={pendingRange[1]}
                      onChange={(newDate) => {
                        const newRange = [pendingRange[0], newDate];
                        setPendingRange(newRange);
                        if (pendingRange[0] && newDate) {
                          setDatePreset("custom");
                          setStartDate(pendingRange[0]);
                          setEndDate(newDate);
                          setCalendarAnchor(null);
                        }
                      }}
                      renderInput={() => null}
                      componentsProps={{ actionBar: { actions: [] } }}
                    />
                  </div>
                </div>
              </LocalizationProvider>
            </Popover>

            {/* Download Report */}
            <Button
              variant="contained"
              onClick={handleDownloadPDFReport}
              disabled={isLoading}
              className="db-export-btn"
              startIcon={<img src={exportPdfIcon} alt="pdf" className="db-export-btn-icon" />}
            >
              {isLoading ? "Generating..." : "Export PDF"}
            </Button>
          </div>
        </div>

        {/* ── Document Type Breakdown Cards ── */}
        <div className="db-doc-cards">
          <DocTypeCard
            icon={packsIcon}
            label="Packs"
            uploaded={loading ? "..." : packsUploaded}
            extracted={loading ? "..." : packsExtracted}
            color="#2AA7E0"
            bgColor="#E9F5FE"
          />
          <DocTypeCard
            icon={formsIcon}
            label="Forms"
            uploaded={loading ? "..." : formsUploaded}
            extracted={loading ? "..." : formsExtracted}
            color="#F2AE33"
            bgColor="#FFEECE"
          />
          <DocTypeCard
            icon={whsIcon}
            label="Withholding Statements"
            uploaded={loading ? "..." : whsUploaded}
            extracted={loading ? "..." : whsExtracted}
            color="#2AA7E0"
            bgColor="#E9F5FE"
          />
          <DocTypeCard
            icon={idCardsIcon}
            label="ID Documents"
            uploaded={loading ? "..." : idDocsUploaded}
            extracted={loading ? "..." : idDocsExtracted}
            color="#F2AE33"
            bgColor="#FFEECE"
          />
        </div>

        {/* ── Active Filter Chip ── */}
        {datePreset !== "all" && (
          <div className="db-filter-chip-container">
            <Chip
              label={`Filtered: ${selectedPresetLabel}`}
              size="small"
              onDelete={() => {
                setDatePreset("all");
                setStartDate(null);
                setEndDate(null);
              }}
              sx={{
                backgroundColor: "#e3f2fd",
                color: "#1565c0",
                fontWeight: 500,
                fontSize: "12px",
                "& .MuiChip-deleteIcon": { color: "#1565c0" },
              }}
            />
          </div>
        )}

        {/* ── Charts Grid ── */}
        <div className="db-charts-grid">
          {/* Extraction Funnel Bar Chart */}
          <div className="db-chart-card">
            <div className="db-chart-card-header">
              <h3 className="db-chart-card-title">File Processing Overview</h3>
              <span className="db-chart-card-subtitle">{selectedPresetLabel}</span>
            </div>
            <div className="db-chart-card-body">
              {loading ? (
                <LoaderIndicator />
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={fileProcessingFunnelData}
                    margin={{ top: 20, right: 20, left: 10, bottom: 8 }}
                    barSize={72}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      horizontal={true}
                      vertical={false}
                      stroke="#f0f0f0"
                    />
                    <XAxis
                      dataKey="name"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 12, fill: "#888" }}
                      interval={0}
                    />
                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 11, fill: "#aaa" }}
                    />
                    <RechartsTooltip
                      content={<CustomTooltip />}
                      cursor={{ fill: "rgba(0,0,0,0.03)" }}
                    />
                    <Bar
                      dataKey="value"
                      radius={[6, 6, 0, 0]}
                      isAnimationActive={true}
                      animationDuration={800}
                    >
                      {fileProcessingFunnelData.map((entry, index) => (
                        <Cell key={`bar-${index}`} fill={entry.color} />
                      ))}
                      <LabelList
                        dataKey="value"
                        position="top"
                        style={{ fontSize: "12px", fontWeight: 400, fill: "#555" }}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* Forms Breakdown Donut Chart */}
          <div className="db-chart-card">
            <div className="db-chart-card-header">
              <h3 className="db-chart-card-title">Document Classification</h3>
              <span className="db-chart-card-subtitle">{selectedPresetLabel}</span>
            </div>
            <div className="db-chart-card-body">
              {loading ? (
                <LoaderIndicator />
              ) : (
                <div className="db-pie-container">
                  {/* Donut */}
                  <div className="db-pie-donut">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={workflowStatusData}
                          cx="50%"
                          cy="50%"
                          innerRadius={72}
                          outerRadius={100}
                          paddingAngle={2}
                          dataKey="value"
                          animationDuration={800}
                          stroke="none"
                        >
                          {workflowStatusData.map((entry, index) => (
                            <Cell key={`pie-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <RechartsTooltip content={<CustomTooltip />} />
                      </PieChart>
                    </ResponsiveContainer>
                    {/* Center Label */}
                    <div className="db-pie-center">
                      <div className="db-pie-center-label">Total</div>
                      <div className="db-pie-center-total">
                        {workflowStatusData.reduce((sum, item) => sum + item.value, 0)}
                      </div>
                    </div>
                  </div>

                  {/* Legend */}
                  <div className="db-pie-legend">
                    {workflowStatusData.map((entry, index) => (
                      <div key={index} className="db-pie-legend-item">
                        <div
                          className="db-pie-legend-dot"
                          style={{ backgroundColor: entry.color }}
                        />
                        <span className="db-pie-legend-name">{entry.name}</span>
                        <span className="db-pie-legend-value">{entry.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
